"use client"

import { useClientTranslation } from "@/modules/App/components/i18n/i18n.client"
import { useMediaQuery } from "@/shared/hooks/use-media-query"
import { getStepNumber, ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { usePathname } from "next/navigation"

import { BreadcrumbItem } from "./breadcrumb-item"
import * as styles from "./breadcrumb.styled"

type BreadcrumbProps = {
  className?: string
}

export const Breadcrumb = ({ className }: BreadcrumbProps) => {
  const { t } = useClientTranslation("global", { keyPrefix: "navbar" })
  const [state] = useActor(newSubscriptionFlowService)
  const hasMultiplePets = state.context.quote?.pets && state.context.quote.pets?.length > 1
  const isViewportBiggerThanMobile = useMediaQuery("(min-width: 1024px)")
  const pathname = usePathname()

  const isStateMatchesPetFlow = pathname.includes(ROUTE_PREFIXES.pet)
  const isStateMatchesUserFlow = pathname.includes(ROUTE_PREFIXES.owner)
  const isStateMatchesPrepackages = pathname.includes(ROUTES.prepackages.path)
  const isStateMatchesLiabilityAddOn = pathname.includes(ROUTES.liabilityAddOn.path)
  const isStateMatchesMedicalHistory = pathname.includes(ROUTES.medicalHistory.path)
  const isStateMatchesTribeQuote = pathname.includes(ROUTES.tribeQuote.path)
  const isStateMatchesParameters = pathname.includes(ROUTES.healthCoverage.path)
  const isStateMatchesQuote = pathname.includes(ROUTES.pricing.path)

  const isStateMatchesModalities = pathname.includes(ROUTES.healthCoverageParam.path)

  const isStateMatchesUserInformationsPart2 = pathname.includes(ROUTE_PREFIXES.owner)
  const isStateMatchesRecap = pathname.includes(ROUTES.recap.path)

  const completedStepsBarWidth = ((getStepNumber(pathname) - 1) / 14) * 100

  const renderInitialSteps = () => (
    <>
      <BreadcrumbItem
        variant={isStateMatchesPetFlow ? "default" : "disabled"}
        stepNumber={1}
        isActive={isStateMatchesPetFlow}
      >
        {t("pet-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesUserFlow ? "default" : "disabled"}
        stepNumber={2}
        isActive={isStateMatchesUserFlow}
      >
        {t("user-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={
          isStateMatchesPrepackages ||
          isStateMatchesLiabilityAddOn ||
          isStateMatchesMedicalHistory ||
          isStateMatchesTribeQuote ||
          isStateMatchesParameters ||
          isStateMatchesModalities ||
          (isStateMatchesQuote && hasMultiplePets)
            ? "default"
            : "disabled"
        }
        stepNumber={4}
        isActive={
          isStateMatchesPrepackages ||
          isStateMatchesLiabilityAddOn ||
          isStateMatchesMedicalHistory ||
          isStateMatchesTribeQuote ||
          isStateMatchesModalities ||
          isStateMatchesParameters ||
          (isStateMatchesQuote && hasMultiplePets) ||
          false
        }
      >
        {t("coverage")}
      </BreadcrumbItem>
    </>
  )

  const renderQuoteSteps = () => (
    <>
      <BreadcrumbItem
        variant={isStateMatchesQuote ? "default" : "disabled"}
        stepNumber={1}
        isActive={isStateMatchesQuote}
      >
        {t("quote")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesUserInformationsPart2 ? "default" : "disabled"}
        stepNumber={2}
        isActive={isStateMatchesUserInformationsPart2}
      >
        {t("user-informations")}
      </BreadcrumbItem>
      <BreadcrumbItem
        variant={isStateMatchesRecap ? "default" : "disabled"}
        stepNumber={4}
        isActive={isStateMatchesRecap}
      >
        {t("recap")}
      </BreadcrumbItem>
    </>
  )

  return (
    <>
      {isViewportBiggerThanMobile ? (
        <>
          <div className={styles.progressBarContainerTop}>
            <div className={styles.progressBarTop}>
              <div
                className={styles.completedStepsBarTop}
                style={{ width: `${completedStepsBarWidth}%`, transition: "all 0.3s ease" }}
              />
              <div className={styles.activeStepTop}>
                <div className={styles.activeStepIndicatorTop} />
              </div>
              <div className={styles.uncompletedStepsBarTop} />
            </div>
            <nav className={styles.root(className)}>
              {isStateMatchesPetFlow ||
              isStateMatchesUserFlow ||
              isStateMatchesPrepackages ||
              isStateMatchesLiabilityAddOn ||
              isStateMatchesMedicalHistory ||
              isStateMatchesModalities ||
              (isStateMatchesTribeQuote && !hasMultiplePets) ||
              isStateMatchesParameters ||
              (hasMultiplePets && isStateMatchesQuote)
                ? renderInitialSteps()
                : renderQuoteSteps()}
            </nav>
          </div>
        </>
      ) : null}
    </>
  )
}
